/**
 * MaintenanceScoreSection - Container for 6-pillar maintenance score display
 *
 * Displays the overall maintenance score with 6 collapsible pillar panels.
 * Each pillar can be expanded to show detailed metrics.
 */

import { useState } from 'react';
import { useTheme } from '../../../contexts/useTheme';
import { Activity } from 'lucide-react';
import { MaintenancePillarPanel } from './MaintenancePillarPanel';
import type {
  MaintenanceScoreResult,
  MaintenancePillarName,
  DriftReport,
} from '../../types/analysis.types';

interface MaintenanceScoreSectionProps {
  maintenanceScore: MaintenanceScoreResult;
  driftData?: DriftReport | null;
  className?: string;
}

// Pillar display order
const pillarOrder: MaintenancePillarName[] = [
  'activity',
  'cicd',
  'issues_prs',
  'dependency',
  'release',
  'metadata',
];

export const MaintenanceScoreSection: React.FC<MaintenanceScoreSectionProps> = ({
  maintenanceScore,
  driftData,
  className = '',
}) => {
  const { theme } = useTheme();
  const [expandedPillars, setExpandedPillars] = useState<Set<MaintenancePillarName>>(new Set());

  const cardBg = theme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.9)';
  const cardBorder = theme === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(203, 213, 225, 0.6)';

  // Sort pillars by the defined order
  const sortedPillars = [...maintenanceScore.pillarScores].sort(
    (a, b) => pillarOrder.indexOf(a.name) - pillarOrder.indexOf(b.name),
  );

  const togglePillar = (name: MaintenancePillarName) => {
    setExpandedPillars((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedPillars(new Set(pillarOrder));
  };

  const collapseAll = () => {
    setExpandedPillars(new Set());
  };

  return (
    <div className={className}>
      <div
        className="rounded-2xl p-6 md:p-8"
        style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
      >
        {/* Header */}
        <div className="mb-6">
          <h3 className="text-xl font-bold text-primary flex items-center gap-2">
            <Activity className="w-6 h-6 text-teal-500" />
            Maintenance Health Analysis
          </h3>
          <p className="text-sm text-secondary mt-1">
            6-pillar assessment of repository maintenance practices
          </p>
        </div>

        {/* Expand/Collapse controls */}
        <div className="flex justify-end gap-2 mb-4">
          <button
            onClick={expandAll}
            className="text-xs text-secondary hover:text-primary transition-colors"
          >
            Expand All
          </button>
          <span className="text-secondary">|</span>
          <button
            onClick={collapseAll}
            className="text-xs text-secondary hover:text-primary transition-colors"
          >
            Collapse All
          </button>
        </div>

        {/* Pillar panels */}
        <div className="space-y-3">
          {sortedPillars.map((pillar) => (
            <MaintenancePillarPanel
              key={pillar.name}
              pillar={pillar}
              isExpanded={expandedPillars.has(pillar.name)}
              onToggle={() => togglePillar(pillar.name)}
              driftData={pillar.name === 'dependency' ? driftData : undefined}
            />
          ))}
        </div>

        {/* Timestamp */}
        <div className="mt-6 text-xs text-secondary text-right">
          Analysis collected: {new Date(maintenanceScore.collectedAt).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceScoreSection;
